import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  HomeIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useUser, useAuthActions, useCanManageInventory, useCanViewReports } from '../store';
import { authApi } from '../services/api';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useUser();
  const { logout } = useAuthActions();
  const canManage = useCanManageInventory();
  const canViewReports = useCanViewReports();

  const navigation = [
    { name: '儀表板', href: '/dashboard', icon: HomeIcon },
    { name: '點貨作業', href: '/inventory', icon: CubeIcon },
    { name: '採購單管理', href: '/purchase-orders', icon: ClipboardDocumentListIcon, requireManage: true },
    { name: '未匹配項目', href: '/unmatched', icon: ExclamationTriangleIcon },
    { name: '批號計數器', href: '/batch-counters', icon: Cog6ToothIcon, requireManage: true },
    { name: '報表分析', href: '/reports', icon: ChartBarIcon, requireReports: true },
  ].filter(item => {
    if (item.requireManage && !canManage) return false;
    if (item.requireReports && !canViewReports) return false;
    return true;
  });

  const handleLogout = async () => {
    try {
      if (user) {
        await authApi.logout(user.id);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      logout();
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex w-full max-w-xs flex-1 flex-col bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            <SidebarContent navigation={navigation} currentPath={location.pathname} />
            <UserSection user={user} onLogout={handleLogout} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <SidebarContent navigation={navigation} currentPath={location.pathname} />
          <UserSection user={user} onLogout={handleLogout} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <div className="relative z-10 flex h-16 flex-shrink-0 bg-white shadow">
          <button
            type="button"
            className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          
          <div className="flex flex-1 justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex flex-1 items-center">
              <h1 className="text-lg font-semibold text-gray-900">
                {navigation.find(item => item.href === location.pathname)?.name || 'GREVIA 點貨系統'}
              </h1>
            </div>
            
            {/* Right side of top bar */}
            <div className="ml-4 flex items-center md:ml-6">
              <div className="flex items-center space-x-3">
                {user?.picture && (
                  <img
                    className="h-8 w-8 rounded-full"
                    src={user.picture}
                    alt={user.name}
                  />
                )}
                <div className="hidden md:block">
                  <div className="text-sm font-medium text-gray-700">{user?.name}</div>
                  <div className="text-xs text-gray-500">{user?.email}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

const SidebarContent = ({ navigation, currentPath }: { navigation: any[], currentPath: string }) => {
  return (
    <div className="flex flex-1 flex-col pt-5 pb-4 overflow-y-auto">
      <div className="flex items-center flex-shrink-0 px-4">
        <img
          className="h-8 w-auto"
          src="/logo.png"
          alt="GREVIA Solution"
        />
        <span className="ml-2 text-lg font-semibold text-gray-900">GREVIA</span>
      </div>
      
      <nav className="mt-8 flex-1 space-y-1 bg-white px-2">
        {navigation.map((item) => {
          const isCurrent = currentPath === item.href;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                isCurrent
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <item.icon
                className={`mr-3 flex-shrink-0 h-6 w-6 ${
                  isCurrent ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                }`}
              />
              {item.name}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

const UserSection = ({ user, onLogout }: { user: any, onLogout: () => void }) => {
  return (
    <div className="flex-shrink-0 border-t border-gray-200 p-4">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          {user?.picture ? (
            <img className="h-10 w-10 rounded-full" src={user.picture} alt="" />
          ) : (
            <UserIcon className="h-10 w-10 text-gray-400" />
          )}
        </div>
        <div className="ml-3 flex-1">
          <p className="text-sm font-medium text-gray-700">{user?.name}</p>
          <p className="text-xs text-gray-500">{user?.role}</p>
        </div>
        <button
          onClick={onLogout}
          className="ml-3 inline-flex items-center px-2 py-2 text-sm text-gray-500 hover:text-gray-700"
          title="登出"
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default Layout;